import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { driverProductSnapshotInfo, formatInitiator } from '@/utils/deviceDetailUtlis';
import { Theme } from '@emotion/react';
import {
    SxProps,
    Box,
    Typography,
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from '@mui/material';
import { DeviceProfile, JobHistoryItem } from '@/types/pages/storeManagement/device';
import PreviewImage from './PreviewImage';
import TableCellBulletList from './TableCellBulletList';
import BaseCopyableText from '../ui/BaseCopyableText';

type DetailFieldProps = {
    label: string;
    value: React.ReactNode;
    title?: string;
    sx?: SxProps<Theme>;
};

const DetailField: FC<DetailFieldProps> = ({ label, value, title, sx }) => (
    <Box sx={{ minWidth: 0, ...sx }}>
        <Typography variant="subtitle2" component="div">
            {label}
        </Typography>

        <Typography
            variant="body2"
            sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
            }}
            title={title}
        >
            {value}
        </Typography>
    </Box>
);

const fieldSx: SxProps<Theme> = { flex: '0 1 1' };

type JobDetailsProps = {
    job: JobHistoryItem;
    profile?: DeviceProfile | null;
    formatUtcToStoreTime: (utcTime?: string | null) => string;
};

type ProductSnapshotEntry = ReturnType<typeof driverProductSnapshotInfo>[number];

const JobDetailRow: FC<JobDetailsProps> = ({ job, profile, formatUtcToStoreTime }) => {
    const { t } = useTranslation('common');
    const productSnapshotList = driverProductSnapshotInfo(job.product_snapshot);
    const initiator = formatInitiator(job.initiator);

    const productColumns = useMemo(
        () => [
            {
                id: 'shelfCodes',
                label: 'Shelf Codes',
                width: 150,
                renderCell: (product: ProductSnapshotEntry) => <TableCellBulletList list={product.shelfCodes} />,
            },
            {
                id: 'campaignCodes',
                label: 'Campaign Files',
                width: 150,
                renderCell: (product: ProductSnapshotEntry) => <TableCellBulletList list={product.campaignCodes} />,
            },
            {
                id: 'selectShelf',
                label: 'Selected Shelf',
                width: 150,
                renderCell: (_product: ProductSnapshotEntry, index: number) => (
                    <BaseCopyableText value={job.selection_snapshot?.items[index]?.planogram?.value} />
                ),
            },
            {
                id: 'selectCampaignSize',
                label: 'Selected Campaign',
                width: 160,
                renderCell: (_product: ProductSnapshotEntry, index: number) => (
                    <BaseCopyableText value={job.selection_snapshot?.items[index]?.campaign?.original_filename} />
                ),
            },
            {
                id: 'subLayout',
                label: 'Sub Layout',
                width: 150,
                renderCell: (_product: ProductSnapshotEntry, index: number) => (
                    <BaseCopyableText value={job.selection_snapshot?.items[index]?.sub_layout?.name} />
                ),
            },
        ],
        [job],
    );

    return (
        <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '3fr 7fr' },
                    gap: 3,
                    alignItems: 'start',
                }}
            >
                <PreviewImage
                    imgPath={job.preview_url}
                    alt={t('History Preview')}
                    profile={profile}
                    maxWidth={250}
                    maxHeight={300}
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                />

                <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <DetailField label={t('Initiator')} value={initiator} title={initiator} />

                    <Box sx={{ display: 'flex', gap: 4 }}>
                        <DetailField
                            sx={fieldSx}
                            label={t('Finish Time')}
                            value={job.status === 'PROCESSING' ? '-' : formatUtcToStoreTime(job.updated_at)}
                        />
                        <DetailField
                            sx={fieldSx}
                            label={t('Layout Name')}
                            value={job.selection_snapshot?.layout?.name || '-'}
                        />
                    </Box>
                </Box>
            </Box>

            {productSnapshotList.length > 0 && (
                <Box sx={{ mt: 2.5 }}>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {productColumns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            sx={{ width: column.width, fontWeight: 600, bgcolor: 'background.paper' }}
                                        >
                                            {t(column.label)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {productSnapshotList.map((product, index) => (
                                    <TableRow
                                        key={`${product.barcode}-${index}`}
                                        sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                                    >
                                        {productColumns.map((column) => (
                                            <TableCell key={column.id} sx={{ width: column.width }}>
                                                {column.renderCell(product, index)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </Box>
    );
};
export default JobDetailRow;
